import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { FeaturedSummary, FeaturedPurchase } from '../types/types';

// A feature-eligible property surfaced by the name typeahead in FeaturedManager.
export interface FeaturedCandidate {
  id: string;
  title: string;
  ii_id: string | null;
  cover_photo_url: string | null;
  city_name: string | null;
}

// Encapsulates the get_agency_featured_summary fetch + activate/deactivate
// RPCs. Used by both PropiedadesView (to filter the main grid + supply action
// handlers to each PropertyCard) and FeaturedManager (the banner UI). Hoisting
// it out of FeaturedManager lets the existing properties grid drive the
// picker UX — no second list, no modal.
export function useFeaturedManagement() {
  const [summary, setSummary] = useState<FeaturedSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [actioningId, setActioningId] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!supabase) return;
    setLoading(true);
    const { data, error } = await supabase.rpc('get_agency_featured_summary');
    if (!error && data && !(data as any).error) {
      setSummary(data as FeaturedSummary);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const featuredIds = useMemo(
    () => new Set((summary?.featured_properties ?? []).map(p => p.id)),
    [summary]
  );

  // Purchases that can still pay for a destaque, soonest-expiring first.
  const purchasesWithSlots = useMemo((): FeaturedPurchase[] => (
    (summary?.purchases ?? [])
      .filter(p => p.available_slots > 0)
      .slice()
      .sort((a, b) => +new Date(a.expire_date) - +new Date(b.expire_date))
  ), [summary]);

  // Which purchase pays is NOT a detail: it sets the property's portal reach
  // (Pro = all portals) AND its featured_until, since the destaque expires with
  // the purchase. So we only auto-pick when there is genuinely nothing to
  // decide. With two or more candidates the caller must ask — silently taking
  // the soonest-expiring one (the old behaviour) could spend a Pro slot on a
  // 5-day run while a 6-month Estándar sat free.
  const needsPurchaseChoice = purchasesWithSlots.length > 1;
  const bestPurchaseForActivation = useMemo((): FeaturedPurchase | null => (
    purchasesWithSlots.length === 1 ? purchasesWithSlots[0] : null
  ), [purchasesWithSlots]);

  // Where an already-destacada property may move: anything with a free slot,
  // plus its current purchase (shown as "actual", never an error to re-pick).
  const switchTargetsFor = useCallback((propertyId: string): FeaturedPurchase[] => {
    const current = (summary?.featured_properties ?? []).find(p => p.id === propertyId)?.purchase_id ?? null;
    return (summary?.purchases ?? [])
      .filter(p => p.available_slots > 0 || p.id === current)
      .slice()
      .sort((a, b) => +new Date(a.expire_date) - +new Date(b.expire_date));
  }, [summary]);

  // Name typeahead for the "destacar" picker — published, not-already-featured
  // properties matched by title or ii_id. RLS scopes to the user's agency, so
  // an agent only finds their agency's properties. min 2 chars keeps it tight.
  const searchCandidates = useCallback(async (query: string): Promise<FeaturedCandidate[]> => {
    if (!supabase) return [];
    const safe = query.trim().replace(/[(),*%]/g, '');
    if (safe.length < 2) return [];
    const { data, error } = await supabase
      .from('properties')
      .select('id, title, ii_id, cover_photo_url, cities(name)')
      .eq('is_deleted', false)
      .eq('publish_status', 'published')
      .eq('is_featured', false)
      .or(`title.ilike.%${safe}%,ii_id.ilike.%${safe}%`)
      .order('title')
      .limit(10);
    if (error || !data) return [];
    return (data as any[]).map(r => ({
      id: r.id,
      title: r.title,
      ii_id: r.ii_id ?? null,
      cover_photo_url: r.cover_photo_url ?? null,
      city_name: r.cities?.name ?? null,
    }));
  }, []);

  // purchaseId is required whenever needsPurchaseChoice is true; callers that
  // omit it there get a no-op rather than an arbitrary slot being spent.
  const activate = useCallback(async (propertyId: string, purchaseId?: string): Promise<boolean> => {
    const chosen = purchaseId ?? bestPurchaseForActivation?.id;
    if (!supabase || !chosen) return false;
    setActioningId(propertyId);
    const { data, error } = await supabase.rpc('activate_featured_property', {
      p_property_id: propertyId,
      p_purchase_id: chosen,
    });
    setActioningId(null);
    if (error) {
      // eslint-disable-next-line no-alert
      alert(`Error: ${error.message}`);
      return false;
    }
    if (data?.error) {
      // eslint-disable-next-line no-alert
      alert(data.error);
      return false;
    }
    await refetch();
    return true;
  }, [bestPurchaseForActivation, refetch]);

  // Move an already-destacada property to a different purchase. One RPC, one
  // transaction — deactivate+activate from here would leave the property with
  // no destaque at all if the second call failed.
  const switchPurchase = useCallback(async (propertyId: string, purchaseId: string): Promise<boolean> => {
    if (!supabase) return false;
    setActioningId(propertyId);
    const { data, error } = await supabase.rpc('switch_featured_purchase', {
      p_property_id: propertyId,
      p_purchase_id: purchaseId,
    });
    setActioningId(null);
    if (error) {
      // eslint-disable-next-line no-alert
      alert(`Error: ${error.message}`);
      return false;
    }
    if (data?.error) {
      // eslint-disable-next-line no-alert
      alert(data.error);
      return false;
    }
    await refetch();
    return true;
  }, [refetch]);

  // Caller is responsible for any confirm dialog before calling — keeps the
  // hook pure so callers can sequence optimistic UI updates around it without
  // a flash on cancel.
  const deactivate = useCallback(async (propertyId: string): Promise<boolean> => {
    if (!supabase) return false;
    setActioningId(propertyId);
    const { data, error } = await supabase.rpc('deactivate_featured_property', {
      p_property_id: propertyId,
    });
    setActioningId(null);
    if (error) {
      // eslint-disable-next-line no-alert
      alert(`Error: ${error.message}`);
      return false;
    }
    if (data?.error) {
      // eslint-disable-next-line no-alert
      alert(data.error);
      return false;
    }
    await refetch();
    return true;
  }, [refetch]);

  const totals = summary?.totals;
  const hasPurchases = (summary?.purchases?.length ?? 0) > 0;

  return useMemo(() => ({
    summary,
    loading,
    refetch,
    featuredIds,
    bestPurchaseForActivation,
    purchasesWithSlots,
    needsPurchaseChoice,
    switchTargetsFor,
    activate,
    switchPurchase,
    deactivate,
    searchCandidates,
    actioningId,
    totals,
    hasPurchases,
  }), [summary, loading, refetch, featuredIds, bestPurchaseForActivation, purchasesWithSlots,
    needsPurchaseChoice, switchTargetsFor, activate, switchPurchase, deactivate, searchCandidates,
    actioningId, totals, hasPurchases]);
}
